import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { db, storage } from '../../../firebase';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router';
import dayjs from 'dayjs';
import * as theme from '../../../theme';
import Heading from '../../../components/Heading';
import { getScanLabel, getScanDisplayLabel } from '../../../contexts/FocusScanContext';
import { getMiniScanLabel } from '../../../contexts/MiniScanContext';
import { Button } from '@mui/material';
import ErrorDisplay from '../../../components/ErrorDisplay';
import Box from '@mui/material/Box';
import FileUploadLink from '../../../components/fileUpload/FileUploadLink';
import TruCard from '../../../components/TruCard';
import ScanStatusList from '../../../components/dashboards/lists/ScanStatusList';
import ResourceItem from '../../../components/dashboards/items/ResourceItem';
import { Remove } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import ScanComments from './ScanComments'; // New import for the extracted component
import TotalPaid from './TotalPaid';
import { generateWellnessReportHTML } from './reports/WellnessReport';
import { generateClientReportHTML } from './reports/ClientReport';
import { generateEmotionsReportHTML } from './reports/EmotionsReport';

export default function Scan() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState({});
  const [scan, setScan] = useState({});
  const [practitioner, setPractitioner] = useState({});
  const [error, setError] = useState('');
  const { clientId, scanId } = useParams();

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    if (scanId) {
      getScan();
    }
  }, [scanId]);

  useEffect(() => {
    if (clientId) {
      getClient();
    }
  }, [clientId]);

  useEffect(() => {
    const fetchPractitioner = async () => {
      if (client.practitionerId) {
        try {
          const practitionerRef = doc(db, 'practitioners', client.practitionerId);
          const practitionerSnap = await getDoc(practitionerRef);
          if (practitionerSnap.exists()) {
            setPractitioner(practitionerSnap.data());
          } else {
            setError('Practitioner not found.');
          }
        } catch (err) {
          setError('Failed to fetch practitioner: ' + err.message);
        }
      }
    };
    fetchPractitioner();
  }, [client]);

  async function downloadFile(fileName) {
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      setError('Popup blocked. Please allow popups for this site.');
      return;
    }

    try {
      const filePath = `clients/${clientId}/scans/${scanId}/${fileName}`;
      const fileRef = ref(storage, filePath);
      const url = await getDownloadURL(fileRef);
      newWindow.location.href = url;
    } catch (err) {
      setError('Failed to load file: ' + err.message);
      newWindow.close();
    }
  }

  async function getScan() {
    try {
      const scanRef = doc(db, 'clients', clientId, 'scans', scanId);
      const docSnap = await getDoc(scanRef);
      setScan(docSnap.data() ? { isFreeScan: false, ...docSnap.data() } : { isFreeScan: false });
    } catch (err) {
      setError('Failed to get scan: ' + err.message);
    }
  }

  async function getClient() {
    try {
      const clientRef = doc(db, 'clients', clientId);
      const docSnap = await getDoc(clientRef);
      setClient(docSnap.data() || {});
    } catch (err) {
      setError('Failed to get client: ' + err.message);
    }
  }

  const clientConcerns = typeof client.concerns === 'string'
    ? client.concerns.split(',').map(c => c.trim()).filter(Boolean)
    : Array.isArray(client.concerns) ? client.concerns : [];

  if (!scan || Object.keys(scan).length === 0) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Scan</div>
        <div>Loading...</div>
      </div>
    );
  }

  const concerns = typeof scan.concern === 'string'
    ? scan.concern.split(',').map(c => c.trim()).filter(Boolean)
    : Array.isArray(scan.concern) ? scan.concern : [];

  const supplementsList = typeof scan.supplements === 'string'
    ? scan.supplements.split(',').map(s => s.trim()).filter(Boolean)
    : Array.isArray(scan.supplements) ? scan.supplements : [];

  const getTrackingUrl = (trackingNumber) => {
    if (trackingNumber.startsWith('1Z')) {
      return `https://www.ups.com/track?track=yes&trackNums=${trackingNumber}`;
    } else {
      return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`;
    }
  };

  return (
    <div style={theme.Container.flexColumnContainer}>
      <ErrorDisplay message={error} type={'error'} clearMessage={clearError} />
      <div style={theme.Container.flexColumn}>
        <Heading
          title={`${client.firstName || ''} ${client.lastName || ''}`}
          onClick={() => navigate(`/clients/${clientId}`)}
          immediateFamily={client.immediateFamily}
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <Heading
            title={
              !scan.cancelled
                ? dayjs(scan.scanDate).format('MMMM D, YYYY')
                : 'SCAN CANCELLED' || ''
            }
            subHeading={true}
            icon={currentUser.role !== 'practitioner' && scan.scanPaid ? 'edit' : ''}
            iconAction={() => navigate(`/clients/${clientId}/scans/${scanId}/upload`)}
          />
          {(scan.scanPaid && currentUser.role !== 'practitioner') ||
          scan.reportsUploaded ? (
            <>
              <TruCard title={'Client Reports'}>
                {scan.reports && scan.reports.clientReport ? (
                  <FileUploadLink
                    key={'clientReport'}
                    label={`${client.firstName || ''} ${client.lastName || ''}`}
                    content={dayjs(
                      scan.reports.clientReport.dateUploaded.toDate()
                    ).format('MM/DD/YYYY h:mm A')}
                    onClick={() => downloadFile(scan.reports.clientReport.fileName)}
                  />
                ) : (
                  // <div style={theme.TruCard.SubText}>Client Report Not Uploaded</div>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FileUploadLink
                      key={'clientReport'}
                      label={`${client.firstName || ''} ${client.lastName || ''}`}
                      content={dayjs(scan.scanDate).format('MM/DD/YYYY')}
                      onClick={() => {
                        if (scan.reportData) {
                          const html = generateClientReportHTML(scan.reportData, `${client.firstName} ${client.lastName}`, `${dayjs(scan.scanDate).format('YYYY.MM.DD')}`);
                          const win = window.open('', '_blank');
                          if (win) {
                            win.document.write(html);
                            win.document.close();
                          } else {
                            alert('Popup blocked. Please allow popups to view the report.');
                          }
                        } else {
                          downloadFile(scan.reports.clientReport.fileName);
                        }
                      }}
                    />
                    {currentUser.role !== 'practitioner' && (
                      <>
                      <Button 
                        variant="contained" 
                        color="secondary" 
                        onClick={() => navigate(`/clients/${clientId}/scans/${scanId}/editClientReport`)}
                        sx={{ ml: 2, width: 'fit-content' }}
                      >
                        Edit
                      </Button>
                      </>
                    )}
                  </Box>
                )}
                {(scan.reports && scan.reports.wellness) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FileUploadLink
                      key={'wellnessScan'}
                      label={'Wellness Scan'}
                      content={dayjs(scan.reports.wellness.dateUploaded.toDate()).format('MM/DD/YYYY h:mm A')}
                      onClick={() => {
                        if (scan.reports.wellness.grokCompleted) {
                          const html = generateWellnessReportHTML(scan.reportData.wellness,  `${client.firstName} ${client.lastName}`, `${dayjs(scan.scanDate).format('YYYY.MM.DD')}`);
                          const win = window.open('', '_blank');
                          if (win) {
                            win.document.write(html);
                            win.document.close();
                          } else {
                            alert('Popup blocked. Please allow popups to view the report.');
                          }
                        } else {
                          downloadFile(scan.reports.wellness.fileName);
                        }
                      }}
                    />
                    {currentUser.role !== 'practitioner' && (scan.reports.wellnessScan ? scan.reports.wellnessScan.grok : scan.reports.wellness.grokCompleted) && (
                      <>
                      <Button 
                        variant="contained" 
                        color="secondary" 
                        onClick={() => navigate(`/clients/${clientId}/scans/${scanId}/editWellnessReport`)}
                        sx={{ ml: 2, width: 'fit-content' }}
                      >
                        Edit
                      </Button>
                      </>
                    )}
                  </Box>
                ) : null}
                {scan.reports && scan.reports.dietGuide ? (
                  <FileUploadLink
                    key={'dietGuide'}
                    label={'Diet Guide'}
                    content={dayjs(
                      scan.reports.dietGuide.dateUploaded.toDate()
                    ).format('MM/DD/YYYY h:mm A')}
                    onClick={() =>
                      downloadFile(scan.reports.dietGuide.fileName)
                    }
                  />
                ) : null}
              </TruCard>
              <TruCard title={'Scan Reports'} styles={{ marginTop: 20 }}>
                {scan.reports && scan.reports.supplementDetail ? (
                  <FileUploadLink
                    key={'supplementDetail'}
                    label={'Supplement (Detail)'}
                    content={dayjs(
                      scan.reports.supplementDetail.dateUploaded.toDate()
                    ).format('MM/DD/YYYY h:mm A')}
                    onClick={() =>
                      downloadFile(scan.reports.supplementDetail.fileName)
                    }
                  />
                ) : null}
                {scan.reports && scan.reports.supplementShortened ? (
                  <FileUploadLink
                    key={'supplementShortened'}
                    label={'Supplement (Shortened)'}
                    content={dayjs(
                      scan.reports.supplementShortened.dateUploaded.toDate()
                    ).format('MM/DD/YYYY h:mm A')}
                    onClick={() =>
                      downloadFile(scan.reports.supplementShortened.fileName)
                    }
                  />
                ) : null}
                {scan.reports && scan.reports.rootCause ? (
                  <FileUploadLink
                    key={'rootCause'}
                    label={'Root Cause'}
                    content={dayjs(
                      scan.reports.rootCause.dateUploaded.toDate()
                    ).format('MM/DD/YYYY h:mm A')}
                    onClick={() => downloadFile(scan.reports.rootCause.fileName)}
                  />
                ) : null}
                {scan.reports && scan.reports.emotions ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FileUploadLink
                      key={'emotions'}
                      label={'Emotions'}
                      content={dayjs(
                        scan.reports.emotions.dateUploaded.toDate()
                      ).format('MM/DD/YYYY h:mm A')}
                      onClick={() => {
                        if (scan.reports.emotions.grokCompleted) {
                          const html = generateEmotionsReportHTML(scan.reportData.emotions,  `${client.firstName} ${client.lastName}`, `${dayjs(scan.scanDate).format('YYYY.MM.DD')}`);
                          const win = window.open('', '_blank');
                          if (win) {
                            win.document.write(html);
                            win.document.close();
                          } else {
                            alert('Popup blocked. Please allow popups to view the report.');
                          }
                        } else {
                          downloadFile(scan.reports.emotions.fileName);
                        }
                      }}
                      // onClick={() => downloadFile(scan.reports.emotions.fileName)}
                    />
                    {currentUser.role !== 'practitioner' && scan.reportData.emotions && (
                      <>
                      <Button 
                        variant="contained" 
                        color="secondary" 
                        onClick={() => navigate(`/clients/${clientId}/scans/${scanId}/editEmotionsReport`)}
                        sx={{ ml: 2, width: 'fit-content' }}
                      >
                        Edit
                      </Button>
                      </>
                    )}
                  </Box>
                ) : null}
                {scan.reports &&
                  scan.reports.focusScans &&
                  scan.reports.focusScans.length > 0 &&
                  scan.reports.focusScans.map((f, i) => (
                    <FileUploadLink
                      key={`focus-${i}`}
                      label={'Focus Scan: ' + getScanDisplayLabel(f.focusScanType)}
                      content={dayjs(f.dateUploaded.toDate()).format(
                        'MM/DD/YYYY h:mm A'
                      )}
                      onClick={() => downloadFile(f.fileName)}
                    />
                  ))}
                {(!scan.reports ||
                  (scan.reports.rootCause === null &&
                    scan.reports.supplementSummary &&
                    scan.reports.supplementDetail &&
                    scan.reports.emotions === null &&
                    scan.reports.focusScans.length <= 0)) && (
                  <div style={theme.TruCard.SubText}>Not Uploaded</div>
                )}
              </TruCard>
              <TruCard title={'Imprint Remedy'} styles={{ marginTop: 20 }}>
                <div style={{ ...theme.Container.flexRow, justifyContent: 'space-around' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100px' }}>
                    <div style={{ fontSize: '40px', fontWeight: 'bold' }}>{scan.reports && scan.reports.numberOfDrops || '-'}</div>
                    <div style={{ fontSize: '14px', color: 'gray' }}>Drops</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100px' }}>
                    <div style={{ fontSize: '40px', fontWeight: 'bold' }}>{scan.reports && scan.reports.numberOfWeeks || '-'}</div>
                    <div style={{ fontSize: '14px', color: 'gray' }}>Weeks</div>
                  </div>
                </div>
              </TruCard>
            </>
          ) : null}
          <ScanStatusList
            scan={scan}
            clientId={clientId}
            scanId={scanId}
            currentUser={currentUser}
            setScan={setScan}
            setError={setError}
          />
          {scan.kitMailed && (
            <TruCard title={'Shipment Tracking Numbers'} styles={{ marginTop: 20 }}>
              {scan.kitMailed.outgoingUSPS && 
                <ResourceItem
                  icon="mail"
                  label="Outgoing Kit Tracking #"
                  actionLink={() => window.open(getTrackingUrl(scan.kitMailed.outgoingUSPS), '_blank')}
                />
              }
              {scan.kitMailed.returnUSPS && 
                <ResourceItem
                  icon="mail"
                  label="Return Kit Tracking #"
                  actionLink={() => window.open(getTrackingUrl(scan.kitMailed.returnUSPS), '_blank')}
                />
              }
              {scan.imprintMailed?.imprintUSPS && 
                <ResourceItem
                  icon="mail"
                  label="Imprint Remedy Tracking #"
                  actionLink={() => window.open(getTrackingUrl(scan.imprintMailed.imprintUSPS), '_blank')}
                />
              }
            </TruCard>
          )}
          <TotalPaid scan={scan} client={client} practitioner={practitioner} />
        </div>
        <div style={theme.Container.flexTwo}>
          <Heading
            title={`Scan Detail`}
            subHeading={true}
            icon={!scan.cancelled && !scan.scanPaid ? 'edit' : null}
            iconAction={() => navigate(`/clients/${clientId}/scans/${scanId}/edit`)}
          />
          <div style={{ ...theme.Container.flexRow, width: '100%', gap: 20 }}>
            <div style={{ ...theme.Container.flexColumn, flex: 1.8, gap: 20 }}>
                <TruCard title={'Areas of Concern'} styles={{ width: '100%' }}>
                  <div style={theme.TruCard.SubTextLeft}>
                    {concerns.length > 0 ? (
                      concerns.map((c, i) => <div key={i}><Remove sx={{ fontSize: 12 }} /> {c}</div>)
                    ) : (
                      'None provided'
                    )}
                  </div>
                </TruCard>
                <TruCard
                  title={'Specific Supplements to Scan For'}
                  styles={{ width: '100%' }}
                >
                  <div style={theme.TruCard.SubTextLeft}>
                    {supplementsList.length > 0 ? (
                      supplementsList.map((s, i) => <div key={i}><Remove sx={{ fontSize: 12 }} /> {s}</div>)
                    ) : (
                      'None provided'
                    )}
                  </div>
                </TruCard>
                <TruCard
                  title={'Specific Concerns/Diagnosis'}
                  styles={{ width: '100%' }}
                >
                  <div style={theme.TruCard.SubTextLeft}>
                    {clientConcerns.length > 0 ? (
                      clientConcerns.map((c, i) => <div key={i}><Remove sx={{ fontSize: 12 }} /> {c}</div>)
                    ) : (
                      'None provided'
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontStyle: 'italic', fontSize: 12 }}>
                    From Client Chart
                  </div>
                </TruCard>
            </div>
            <div style={{ ...theme.Container.flexColumn, flex: 1, gap: 20 }}>
                <TruCard title={'Focus Scans Requested'}>
                  <div style={theme.TruCard.SubText}>
                    {getScanLabel(scan.firstFocusScan) || ''} <br />
                    {getScanLabel(scan.secondFocusScan) || ''}
                  </div>
                </TruCard>
                <TruCard title={'Mini Scans Requested'}>
                  <div style={theme.TruCard.SubText}>
                    {getMiniScanLabel(scan.firstMiniScan) || ''} <br />
                    {getMiniScanLabel(scan.secondMiniScan) || ''}
                  </div>
                </TruCard>
                <TruCard title={'Focus Scans Completed'}>
                  <div style={theme.TruCard.SubText}>
                    {scan.reports &&
                      scan.reports.focusScans &&
                      scan.reports.focusScans.length > 0 &&
                      scan.reports.focusScans.map((f) => (
                        <div key={f.focusScanType}>
                          {getScanDisplayLabel(f.focusScanType) || ''}
                        </div>
                      ))}
                    {(!scan.reports ||
                      !scan.reports.focusScans ||
                      scan.reports.focusScans.length === 0) && <br />}
                  </div>
                </TruCard>
            </div>
          </div>
          <ScanComments
            scan={scan}
            setScan={setScan}
            practitionerId={client.practitionerId}
            clientId={clientId}
            scanId={scanId}
            currentUser={currentUser}
            setError={setError}
            clientName={`${client.firstName} ${client.lastName}`}
          />
        </div>
      </div>
      <div
        style={{
          marginTop: 20,
          padding: 20,
          textAlign: 'center',
          fontSize: `0.85em`,
          color: theme.Colors.GrayMedium,
        }}
      >
        <div style={{ marginBottom: 6 }}>
          <span style={{ color: theme.Colors.Purple, fontWeight: 600 }}>
            Created:
          </span>{' '}
          {scan.dateCreated
            ? dayjs(scan.dateCreated.toDate()).format('MMMM D, YYYY h:mm A')
            : 'N/A'}
        </div>
        <span style={{ color: theme.Colors.Purple, fontWeight: 600 }}>
          Modified:
        </span>{' '}
        {scan.dateModified
          ? dayjs(scan.dateModified.toDate()).format('MMMM D, YYYY h:mm A')
          : 'N/A'}
      </div>
    </div>
  );
}